import { useState, useEffect } from "react";
import { Button } from "@app/components/shared/Button";
import {
  getTradingSystemPayload,
  updateCustomPairs,
} from "@app/services/tradingSystemApi";
import { DirectExecutionTab } from "./DirectExecutionTab";

interface CustomPairsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

type TabType = "priority" | "direct";

export function CustomPairsModal({
  isOpen,
  onClose,
  onUpdate,
}: CustomPairsModalProps): JSX.Element | null {
  const [activeTab, setActiveTab] = useState<TabType>("priority");
  const [pairs, setPairs] = useState<string[]>([]);
  const [newPair, setNewPair] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPairs();
    }
  }, [isOpen]);

  async function loadPairs(): Promise<void> {
    setIsLoading(true);
    setError(null);
    try {
      const payload = await getTradingSystemPayload();
      setPairs([...payload.custom_pairs]);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load custom pairs";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await updateCustomPairs({ pairs });
      await onUpdate();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save custom pairs";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }

  function handleAddPair(): void {
    const trimmed = newPair.trim().toUpperCase();
    if (!trimmed) {
      return;
    }
    if (pairs.includes(trimmed)) {
      setError(`Pair ${trimmed} already exists`);
      return;
    }
    if (!/^[A-Z0-9]+\/[A-Z0-9]+$/.test(trimmed)) {
      setError("Invalid pair format. Use format: BTC/USDT");
      return;
    }
    setPairs([...pairs, trimmed]);
    setNewPair("");
    setError(null);
  }

  function handleRemovePair(pairToRemove: string): void {
    setPairs(pairs.filter((p) => p !== pairToRemove));
  }

  function handleClearAll(): void {
    setPairs([]);
  }

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      handleAddPair();
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)",
        }}
        role="presentation"
        onClick={onClose}
      />
      <aside
        style={{
          position: "relative",
          zIndex: 2001,
          background: "var(--color-background-surface)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-lg)",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}
        role="dialog"
        aria-modal="true"
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            padding: "20px 24px",
            borderBottom: "1px solid var(--color-border-subtle)",
          }}
        >
          <div>
            <h2 style={{ margin: 0, marginBottom: "8px", fontSize: "1.25rem", fontWeight: 600 }}>
              Trading Pairs & Direct Execution
            </h2>
            <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--color-text-secondary)" }}>
              Add priority pairs for queue or inject trades directly for immediate execution.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--color-text-primary)",
              cursor: "pointer",
              fontSize: "1.5rem",
              lineHeight: 1,
              padding: "4px",
              opacity: 0.7,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
          >
            ✕
          </button>
        </header>
        
        {/* Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid var(--color-border-subtle)",
            padding: "0 24px",
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab("priority")}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: activeTab === "priority" ? "2px solid var(--color-primary)" : "2px solid transparent",
              color: activeTab === "priority" ? "var(--color-primary)" : "var(--color-text-secondary)",
              cursor: "pointer",
              padding: "12px 16px",
              fontSize: "0.875rem",
              fontWeight: activeTab === "priority" ? 600 : 400,
            }}
          >
            Priority Pairs
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("direct")}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: activeTab === "direct" ? "2px solid var(--color-primary)" : "2px solid transparent",
              color: activeTab === "direct" ? "var(--color-primary)" : "var(--color-text-secondary)",
              cursor: "pointer",
              padding: "12px 16px",
              fontSize: "0.875rem",
              fontWeight: activeTab === "direct" ? 600 : 400,
            }}
          >
            Direct Execution
          </button>
        </div>

        <div style={{ padding: "24px" }}>
          {activeTab === "priority" && (
            <>
              {isLoading ? (
                <div className="empty-state">Loading pairs...</div>
              ) : (
                <div className="configuration-section">
              <div className="configuration-grid">
                <div className="config-item" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="new-pair">
                    <strong>Add New Pair</strong>
                  </label>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      id="new-pair"
                      type="text"
                      placeholder="BTC/USDT"
                      value={newPair}
                      onChange={(e) => setNewPair(e.target.value)}
                      onKeyPress={handleKeyPress}
                      style={{ flex: 1 }}
                    />
                    <Button type="button" variant="primary" onClick={handleAddPair}>
                      Add
                    </Button>
                  </div>
                  <small style={{ color: "var(--color-text-secondary)" }}>
                    Format: BASE/QUOTE (e.g., BTC/USDT, ETH/USDT)
                  </small>
                </div>
              </div>

              {error && (
                <div className="empty-state" style={{ color: "var(--color-error)", marginTop: "16px" }}>
                  {error}
                </div>
              )}

              {pairs.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h3>Selected Pairs ({pairs.length})</h3>
                    <Button type="button" variant="ghost" onClick={handleClearAll}>
                      Clear All
                    </Button>
                  </div>
                  <div className="configuration-grid">
                    {pairs.map((pair) => (
                      <div key={pair} className="config-item">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>{pair}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={() => handleRemovePair(pair)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pairs.length === 0 && (
                <div className="empty-state" style={{ marginTop: "24px" }}>
                  No custom pairs selected. System will use Smart AI selection based on smart_pair_selection.md
                </div>
              )}

              <div className="config-actions" style={{ marginTop: "24px" }}>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Pairs"}
                </Button>
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
              </div>
                </div>
              )}
            </>
          )}
          {activeTab === "direct" && (
            <DirectExecutionTab
              onSuccess={() => {
                if (onUpdate) {
                  onUpdate();
                }
              }}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

